import { api } from './api';
import type {
  Audience,
  AudienceSegment,
  Cadence,
  SegmentPrompt,
  MeasurementRun,
  MeasurementResult,
  MeasurementFact,
  SegmentCoverageRollup,
  MeasurementSynthesis,
} from '@traaker/shared';

/** GEO Measurement — Audiences / Segments / Prompts (Phase 4a). */
export interface AudienceScore {
  audienceId: string;
  score: number;
  prevScore?: number;
  /** Up to the last 6 completed runs' scores, oldest-first (card sparkline, item n). */
  history: number[];
}

export const audiencesApi = {
  list: (brandId: string): Promise<Audience[]> =>
    api.get<Audience[]>(`/brands/${brandId}/audiences`),

  /** Latest + previous run score per audience (list-card score + trend). */
  scores: (brandId: string): Promise<AudienceScore[]> =>
    api.get<AudienceScore[]>(`/brands/${brandId}/audiences/scores`),

  create: (brandId: string, name: string): Promise<Audience> =>
    api.post<Audience>(`/brands/${brandId}/audiences`, { name }),

  get: (brandId: string, audienceId: string): Promise<Audience> =>
    api.get<Audience>(`/brands/${brandId}/audiences/${audienceId}`),

  update: (
    brandId: string,
    audienceId: string,
    body: { name?: string; segments?: AudienceSegment[]; measureCadence?: Cadence },
  ): Promise<Audience> => api.put<Audience>(`/brands/${brandId}/audiences/${audienceId}`, body),

  remove: (brandId: string, audienceId: string): Promise<{ ok: boolean }> =>
    api.delete(`/brands/${brandId}/audiences/${audienceId}`),

  generatePrompts: (
    brandId: string,
    audienceId: string,
    segmentId: string,
  ): Promise<SegmentPrompt[]> =>
    api.post<SegmentPrompt[]>(
      `/brands/${brandId}/audiences/${audienceId}/segments/${segmentId}/generate`,
      {},
    ),

  /** Import a segment's prompts from a CSV — REPLACES its existing prompts. */
  importPrompts: (
    brandId: string,
    audienceId: string,
    segmentId: string,
    csv: string,
  ): Promise<{ prompts: SegmentPrompt[]; imported: number; dropped: { tooShort: number; tooLong: number; duplicates: number } }> =>
    api.post(
      `/brands/${brandId}/audiences/${audienceId}/segments/${segmentId}/prompts/import`,
      { csv },
    ),

  listPrompts: (brandId: string, audienceId: string, segmentId?: string): Promise<SegmentPrompt[]> =>
    api.get<SegmentPrompt[]>(
      `/brands/${brandId}/audiences/${audienceId}/prompts${segmentId ? `?segmentId=${segmentId}` : ''}`,
    ),

  updatePrompt: (
    brandId: string,
    audienceId: string,
    segmentId: string,
    promptId: string,
    body: { text?: string; active?: boolean },
  ): Promise<{ ok: boolean }> =>
    api.put(
      `/brands/${brandId}/audiences/${audienceId}/segments/${segmentId}/prompts/${promptId}`,
      body,
    ),

  // ---- run + results (Phase 4b) ----
  startRun: (brandId: string, audienceId: string): Promise<MeasurementRun> =>
    api.post<MeasurementRun>(`/brands/${brandId}/audiences/${audienceId}/run`, {}),

  runProgress: (brandId: string, audienceId: string, runId?: string): Promise<MeasurementRun | null> =>
    api.get<MeasurementRun | null>(
      `/brands/${brandId}/audiences/${audienceId}/run/progress${runId ? `?runId=${runId}` : ''}`,
    ),

  /** Run history for the "Historique" tab (trend chart + runs table). */
  listRuns: (brandId: string, audienceId: string): Promise<MeasurementRun[]> =>
    api.get<MeasurementRun[]>(`/brands/${brandId}/audiences/${audienceId}/runs`),

  /** Cached run synthesis (deterministic stats + LLM recs); `force` regenerates. */
  runSynthesis: (
    brandId: string,
    audienceId: string,
    runId: string,
    force = false,
  ): Promise<MeasurementSynthesis> =>
    api.get<MeasurementSynthesis>(
      `/brands/${brandId}/audiences/${audienceId}/run/${runId}/synthesis${force ? '?force=1' : ''}`,
    ),

  runRollups: (brandId: string, audienceId: string, runId: string): Promise<SegmentCoverageRollup[]> =>
    api.get<SegmentCoverageRollup[]>(
      `/brands/${brandId}/audiences/${audienceId}/run/${runId}/rollups`,
    ),

  /** Minimal fact rows for the Synthèse client-side filter dashboard (item l). */
  runFacts: (brandId: string, audienceId: string, runId: string): Promise<MeasurementFact[]> =>
    api.get<MeasurementFact[]>(`/brands/${brandId}/audiences/${audienceId}/run/${runId}/facts`),

  /** Paginated result list (light rows). */
  runResults: (
    brandId: string,
    audienceId: string,
    runId: string,
    opts: { segmentId?: string; coverage?: string; zone?: string; cursor?: string; limit?: number } = {},
  ): Promise<{ items: MeasurementResult[]; nextCursor?: string }> => {
    const qs = new URLSearchParams();
    if (opts.segmentId) qs.set('segmentId', opts.segmentId);
    if (opts.coverage) qs.set('coverage', opts.coverage);
    if (opts.zone) qs.set('zone', opts.zone);
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return api.get(`/brands/${brandId}/audiences/${audienceId}/run/${runId}/results${q ? `?${q}` : ''}`);
  },

  /** One full result (AI answer + sources) for the response panel. */
  runResult: (
    brandId: string,
    audienceId: string,
    runId: string,
    segmentId: string,
    promptId: string,
    engine: string,
  ): Promise<MeasurementResult | null> => {
    const qs = new URLSearchParams({ segmentId, promptId, engine });
    return api.get(`/brands/${brandId}/audiences/${audienceId}/run/${runId}/result?${qs}`);
  },
};
